import { ApiError } from "../api/types";

/**
 * Direct-fetch client for the extended login methods (TOTP, magic-link, passkey)
 * and the account-security surface. Kept SEPARATE from the ElaborateApi adapter:
 * these are same-origin, session-cookie flows that require the real backend.
 * Passwords + the session (`login`/`logout`/`me`) still ride the adapter.
 *
 * The passkey helpers implement the browser side of the `@norkron/web-passkey`
 * wire contract inline (base64url + `navigator.credentials`), so the app has no
 * new npm dependency; swapping in `@norkron/web-passkey` (identical endpoints +
 * payload shapes) is a drop-in replacement.
 */

async function call<T>(
  path: string,
  opts: { method?: string; body?: unknown } = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch("/api" + path, {
      method: opts.method ?? "POST",
      credentials: "include",
      headers: opts.body ? { "Content-Type": "application/json" } : undefined,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    throw new ApiError(0, "network_error", (e as Error).message);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, `http_${res.status}`, res.statusText);
  }
  return data as T;
}

function safeJson(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

// ── the pre-login capability probe ─────────────────────────────────────────
/**
 * Which login methods THIS deployment actually serves. Server-derived
 * (`GET /api/auth/methods`, unauthenticated): Google/Microsoft follow the
 * configured OAuth client, `magic_link` follows the transactional mailer. The
 * login screen renders a provider ONLY when its flag is true — an unserved
 * provider is absent, never a disabled button.
 */
export interface AuthMethods {
  password: boolean;
  passkey: boolean;
  google: boolean;
  microsoft: boolean;
  magic_link: boolean;
}

/** The safe floor when the probe cannot be reached: credential login only. */
export const PASSWORD_ONLY: AuthMethods = {
  password: true,
  passkey: false,
  google: false,
  microsoft: false,
  magic_link: false,
};

/** Fetch the advertised methods. Never throws — an unreachable/garbled probe
 *  degrades to `PASSWORD_ONLY` so the credential form always renders. */
export async function fetchAuthMethods(): Promise<AuthMethods> {
  try {
    const raw = await call<Partial<AuthMethods> | null>("/auth/methods", {
      method: "GET",
    });
    if (!raw || typeof raw !== "object") return PASSWORD_ONLY;
    return {
      password: raw.password !== false,
      passkey: raw.passkey === true,
      google: raw.google === true,
      microsoft: raw.microsoft === true,
      magic_link: raw.magic_link === true,
    };
  } catch {
    return PASSWORD_ONLY;
  }
}

// ── OAuth SSO (full-page redirects — no fetch) ─────────────────────────────
/** Hand the browser to the provider's authorization endpoint. A full-page
 *  navigation (not a fetch): the OAuth code exchange lands back on
 *  `/api/auth/oauth/:provider/callback`, which sets the session cookie. */
export function startOAuth(provider: "google" | "microsoft"): void {
  window.location.assign(`/api/auth/oauth/${provider}/start`);
}

// ── magic-link ─────────────────────────────────────────────────────────────
export function requestMagicLink(email: string): Promise<void> {
  return call<void>("/auth/magic/request", { body: { email } });
}

// ── TOTP ───────────────────────────────────────────────────────────────────
export interface TotpEnrollment {
  otpauth_uri: string;
  manual_key: string;
  recovery_codes: string[];
}
export function totpVerify(code: string): Promise<{ user: unknown }> {
  return call("/auth/totp/verify", { body: { code } });
}
export function totpStatus(): Promise<{ enrolled: boolean }> {
  return call("/auth/totp/status", { method: "GET" });
}
export function totpEnroll(): Promise<TotpEnrollment> {
  return call("/auth/totp/enroll");
}
export function totpConfirm(code: string): Promise<{ confirmed: boolean }> {
  return call("/auth/totp/confirm", { body: { code } });
}
export function totpDisable(code: string): Promise<{ disabled: boolean }> {
  return call("/auth/totp/disable", { body: { code } });
}

// ── passkeys (WebAuthn — @norkron/web-passkey contract, inline) ─────────────
export interface PasskeyInfo {
  id: number;
  nickname: string;
  aaguid: string;
  created_at: string;
  last_used_at: string | null;
}
export function listPasskeys(): Promise<PasskeyInfo[]> {
  return call("/auth/passkey/credentials", { method: "GET" });
}
export function deletePasskey(id: number): Promise<void> {
  return call(`/auth/passkey/credentials/${id}`, { method: "DELETE" });
}

/** True when the platform can do WebAuthn at all. */
export function passkeysSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined"
  );
}

function b64urlToBuf(s: string): ArrayBuffer {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}
function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Enroll a new passkey for the signed-in user (register begin → create → finish). */
export async function registerPasskey(nickname: string): Promise<boolean> {
  const options = await call<CredentialCreationOptionsJSON>(
    "/auth/passkey/register/begin",
  );
  const publicKey = toCreationOptions(options);
  const cred = (await navigator.credentials.create({
    publicKey,
  })) as PublicKeyCredential | null;
  if (!cred) return false;
  const att = cred.response as AuthenticatorAttestationResponse;
  const result = await call<{ verified: boolean }>(
    "/auth/passkey/register/finish",
    {
      body: {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        clientExtensionResults: {},
        response: {
          clientDataJSON: bufToB64url(att.clientDataJSON),
          attestationObject: bufToB64url(att.attestationObject),
          transports: att.getTransports?.() ?? [],
        },
        nickname,
      },
    },
  );
  return result.verified === true;
}

/** Discoverable passkey login (authenticate begin → get → finish). Sets the
 *  session cookie on success. */
export async function loginWithPasskey(): Promise<boolean> {
  const options = await call<CredentialRequestOptionsJSON>(
    "/auth/passkey/authenticate/begin",
  );
  const publicKey = toRequestOptions(options);
  const cred = (await navigator.credentials.get({
    publicKey,
  })) as PublicKeyCredential | null;
  if (!cred) return false;
  const asr = cred.response as AuthenticatorAssertionResponse;
  const result = await call<{ verified: boolean }>(
    "/auth/passkey/authenticate/finish",
    {
      body: {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        clientExtensionResults: {},
        response: {
          clientDataJSON: bufToB64url(asr.clientDataJSON),
          authenticatorData: bufToB64url(asr.authenticatorData),
          signature: bufToB64url(asr.signature),
          userHandle: asr.userHandle ? bufToB64url(asr.userHandle) : "",
        },
      },
    },
  );
  return result.verified === true;
}

// ── server-JSON → WebAuthn option shapes (base64url decode) ─────────────────
interface CredentialCreationOptionsJSON {
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: Array<{ type: "public-key"; alg: number }>;
  timeout?: number;
  excludeCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
}
interface CredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId: string;
  allowCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  userVerification?: UserVerificationRequirement;
}

function toCreationOptions(o: CredentialCreationOptionsJSON): PublicKeyCredentialCreationOptions {
  return {
    rp: o.rp,
    user: {
      id: b64urlToBuf(o.user.id),
      name: o.user.name,
      displayName: o.user.displayName,
    },
    challenge: b64urlToBuf(o.challenge),
    pubKeyCredParams: o.pubKeyCredParams,
    timeout: o.timeout,
    excludeCredentials: o.excludeCredentials?.map((c) => ({
      type: c.type,
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
    authenticatorSelection: o.authenticatorSelection,
    attestation: o.attestation,
  };
}
function toRequestOptions(o: CredentialRequestOptionsJSON): PublicKeyCredentialRequestOptions {
  return {
    challenge: b64urlToBuf(o.challenge),
    timeout: o.timeout,
    rpId: o.rpId,
    allowCredentials: o.allowCredentials?.map((c) => ({
      type: c.type,
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
    userVerification: o.userVerification,
  };
}
